import React from 'react';
import { Box, Typography, Stack, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import styles from './MagnifierImageList.module.css';
import { useSiteContent } from '../context/SiteContentContext';
import { COLORS } from '../theme/colors';

export default function MagnifierImageList({ gallery: galleryProp, editable, onAddImage, onDeleteImage }) {
    const { gallery: contextGallery } = useSiteContent();
    const gallery = galleryProp ?? contextGallery;
    return (
        <Box sx={{ padding: { sm: 4, md: 8 }, paddingRight: { sm: 0, md: 0 }, paddingLeft: { sm: 0, md: 8 }, background: 'linear-gradient(90deg, rgba(255, 255, 255, 1) 0%, rgba(212, 210, 210, 0.76) 49%, rgba(255, 255, 255, 1) 100%)', }}>
            <Box sx={{ padding: 4, paddingRight: { sm: 4, md: 6 }, border: 1, borderColor: 'divider', borderTopLeftRadius: 25, borderBottomLeftRadius: 25, backgroundColor: 'background.default' }}>
                <Box sx={{ width: { sm: '100%' }, display: 'flex' }}>
                    <Stack>
                        <Typography
                            component="h2"
                            variant="h4"
                            gutterBottom
                            sx={{ color: 'text.primary' }}
                        >
                            Gallery of CNC machined parts
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{ color: 'text.secondary', mb: { xs: 2, sm: 4 } }}
                        >
                            Here’s a small selection of the CNC machined prototypes and end-use parts we’ve produced for our customers.
                        </Typography>
                    </Stack>
                </Box>
                <Box className={styles.magnifierContainer} sx={{ padding: 2 }}>
                    {gallery.map((img, index) => (
                        <Box
                            key={img.publicId || index}
                            sx={{
                                position: 'relative',
                                flex: { xs: '0 0 78%', sm: 1 },
                                scrollSnapAlign: 'start',
                                height: '100%',
                                transition: 'flex 0.5s',
                                '&:hover': { flex: 3 },
                                '&:hover .delete-photo': { opacity: 1 },
                            }}
                        >
                            <img
                                src={img.url}
                                width={img.width}
                                height={img.height}
                                alt={img.alt || `CNC machined part ${index + 1}`}
                                loading="lazy"
                                style={img.pending ? { opacity: 0.6 } : undefined}
                            />
                            {img.pending && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        bottom: 4,
                                        left: 4,
                                        px: 0.75,
                                        py: 0.25,
                                        borderRadius: 1,
                                        bgcolor: COLORS.overlay,
                                        color: 'white',
                                        fontSize: 11,
                                    }}
                                >
                                    Not saved yet
                                </Box>
                            )}
                            {editable && !img.pending && !img.publicId && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        bottom: 4,
                                        left: 4,
                                        px: 0.75,
                                        py: 0.25,
                                        borderRadius: 1,
                                        bgcolor: COLORS.overlay,
                                        color: 'white',
                                        fontSize: 11,
                                    }}
                                >
                                    Default photo
                                </Box>
                            )}
                            {editable && img.publicId && (
                                <IconButton
                                    className="delete-photo"
                                    aria-label={`Remove ${img.alt || 'photo'}`}
                                    onClick={() => onDeleteImage(img.publicId)}
                                    size="small"
                                    sx={{
                                        position: 'absolute',
                                        top: 4,
                                        right: 4,
                                        bgcolor: 'rgba(0,0,0,0.6)',
                                        color: 'white',
                                        opacity: { xs: 1, sm: 0 },
                                        transition: 'opacity 0.15s',
                                        '&:hover': { bgcolor: 'rgba(0,0,0,0.8)' },
                                    }}
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            )}
                        </Box>
                    ))}
                    {editable && (
                        <Box
                            component="label"
                            sx={{
                                flex: 1,
                                height: '100%',
                                minWidth: 80,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 1,
                                border: '2px dashed',
                                borderColor: 'divider',
                                borderRadius: 1,
                                cursor: 'pointer',
                                color: 'text.secondary',
                                '&:hover': { borderColor: 'primary.main', color: 'primary.main' },
                            }}
                        >
                            <AddPhotoAlternateIcon />
                            <Typography variant="caption">Add photo</Typography>
                            <input
                                id="gallery-image"
                                name="galleryImage"
                                type="file"
                                accept="image/*"
                                hidden
                                onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) onAddImage(file);
                                    e.target.value = '';
                                }}
                            />
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
}
